// First-run KEY GATE — shown on the LOCAL manager BEFORE the login screen when
// the box runs in remote-store mode but has no local-access key yet. The
// operator creates a key in the PRODUCTION portal (Keys screen), pastes it here,
// and the box connects to the shared remote store. Only then does login appear.
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useConnectRemote } from './hooks';

const ID = 'em-mgr--key-gate';

export function KeyGate({ remoteUrl }: { remoteUrl?: string | null }) {
  const [key, setKey] = useState('');
  const connect = useConnectRemote();

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!key.trim()) return;
    connect.mutate({ remoteKey: key.trim() });
  }

  return (
    <div
      data-id={ID}
      className="flex min-h-full items-center justify-center bg-slate-50 p-6 dark:bg-slate-950"
    >
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Connect to the remote store
        </h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
          This local manager reads and writes the shared fleet store on the
          production server. Paste a <span className="font-medium">local access key</span>{' '}
          created in the production portal (Keys) to connect.
          {remoteUrl && (
            <>
              {' '}
              <span className="font-mono text-xs">{remoteUrl}</span>
            </>
          )}
        </p>

        <form onSubmit={submit} className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="text-slate-500">Local access key</span>
            <input
              data-id={`${ID}--key`}
              className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 font-mono text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="cvnk_…"
              autoFocus
            />
          </label>
          <button
            type="submit"
            data-id={`${ID}--connect`}
            disabled={connect.isPending || !key.trim()}
            className="w-full rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-300"
          >
            {connect.isPending ? 'Connecting…' : 'Connect'}
          </button>
          {connect.isError && (
            <p data-id={`${ID}--error`} className="text-sm text-red-600 dark:text-red-400">
              {connect.error instanceof Error ? connect.error.message : 'Could not connect'}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
